import React, { useEffect, useState } from 'react';
import Button from '../Button/Button';
import { handleSaveDraft, handleValidatedNext } from '../../utils/commonHandlers';

import './CreationArticle.css'

interface Article {
  id: string;
  nom: string;
  code: number;
}

interface Section {
  id: string;
  name: string;
  articles: Article[];
}

interface CMStepThreeProps {
  formData: {
    sections?: Section[];
    nomOperation: string;
    [key: string]: any;
  };
  setFormData?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  viewMode?: boolean;
}

const CMStepThree: React.FC<CMStepThreeProps> = ({
  formData,
  setFormData,
  onNext,
  onPrevious,
  viewMode = false
}) => {
  const [newSectionName, setNewSectionName] = useState('');
  const [newArticleName, setNewArticleName] = useState('');
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [loadingSectionId, setLoadingSectionId] = useState<string | null>(null);
  const [loadingArticleId, setLoadingArticleId] = useState<string | null>(null);
  const [isAddingSectionLoading, setIsAddingSectionLoading] = useState(false);

  const sections = formData.sections || [];

  // Fonction utilitaire pour faire disparaître les messages après 2 secondes
  const clearMessageAfterDelay = (messageType: 'success' | 'error') => {
    setTimeout(() => {
      if (messageType === 'success') {
        setSuccessMsg('');
      } else {
        setError(null);
      }
    }, 2000);
  };

  // Récupérer l'id de l'opération depuis l'URL
  const getOperationId = () => {
    const pathParts = window.location.pathname.split('/');
    // Cherche l'id après "operation" ou "consultation"
    const opIdx = pathParts.findIndex(p => p === 'operation' || p === 'consultation');
    if (opIdx !== -1 && pathParts[opIdx + 1]) {
      return pathParts[opIdx + 1];
    }
    // Fallback: 3e segment (ex: /consultation/:id)
    return pathParts[2] || '';
  };

  // Ajout d'une section avec appel API
  const handleAddSection = async () => {
    if (!viewMode && setFormData && newSectionName) {
      const operationId = getOperationId();
      if (!operationId) {
        setError("ID d'opération introuvable.");
        clearMessageAfterDelay('error');
        return;
      }
      setIsAddingSectionLoading(true);
      setError(null);
      setSuccessMsg('');
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${operationId}/section`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ name: newSectionName })
        });
        if (!response.ok) throw new Error("Erreur lors de la création de la section");
        const data = await response.json();
        // Utiliser l'id et le nom retournés par l'API
        const newSection: Section = {
          id: data.data?.id,
          name: data.data?.nom || newSectionName,
          articles: []
        };
        setFormData({
          ...formData,
          sections: [...sections, newSection]
        });
        setNewSectionName('');
        setActiveSection(newSection.id);
        setSuccessMsg("Section ajoutée !");
        clearMessageAfterDelay('success');
      } catch (e: any) {
        setError(e.message || "Erreur lors de l'ajout de la section");
        clearMessageAfterDelay('error');
      } finally {
        setIsAddingSectionLoading(false);
      }
    }
  };

  // Ajout d'un article avec appel API
  const handleAddArticle = async (sectionId: string) => {
    if (!viewMode && setFormData && newArticleName) {
      const operationId = getOperationId();
      if (!operationId) {
        setError("ID d'opération introuvable.");
        clearMessageAfterDelay('error');
        return;
      }
      setLoadingArticleId(sectionId);
      setError(null);
      setSuccessMsg('');
      try {
        const token = localStorage.getItem('token');

        // Calculer le prochain numéro de code
        const allCodes = sections.flatMap(section =>
          section.articles.map(article => article.code)
        );
        const nextCode = allCodes.length > 0
          ? Math.max(...allCodes.map(n => Number(n) || 0)) + 1
          : 1;

        const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${operationId}/article`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            nom: newArticleName,
            code: nextCode,
            id_section: sectionId // L'API attend "id_section"
          })
        });

        if (!response.ok) throw new Error("Erreur lors de la création de l'article");

        const data = await response.json();
        const newArticle: Article = {
          id: data.data?.id?.toString() || Date.now().toString(),
          nom: data.data?.nom || newArticleName,
          code: data.data?.code || nextCode // L'API retourne "code"
        };

        setFormData({
          ...formData,
          sections: sections.map(section =>
            section.id === sectionId
              ? { ...section, articles: [...section.articles, newArticle] }
              : section
          )
        });
        setNewArticleName('');
        setSuccessMsg("Article ajouté !");
        clearMessageAfterDelay('success');
      } catch (e: any) {
        setError(e.message || "Erreur lors de l'ajout de l'article");
        clearMessageAfterDelay('error');
      } finally {
        setLoadingArticleId(null);
      }
    }
  };

  // Suppression d'une section avec appel API
  const handleDeleteSection = async (sectionId: string) => {
    if (!viewMode && setFormData) {
      const operationId = getOperationId();
      if (!operationId) {
        setError("ID d'opération introuvable.");
        clearMessageAfterDelay('error');
        return;
      }
      setLoadingSectionId(sectionId);
      setError(null);
      setSuccessMsg('');
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${operationId}/section/${sectionId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) throw new Error("Erreur lors de la suppression de la section");

        // Supprimer la section du state local après confirmation API
        setFormData({
          ...formData,
          sections: sections.filter(section => section.id !== sectionId)
        });
        setSuccessMsg("Section supprimée !");
        clearMessageAfterDelay('success');
      } catch (e: any) {
        setError(e.message || "Erreur lors de la suppression de la section");
        clearMessageAfterDelay('error');
      } finally {
        setLoadingSectionId(null);
      }
    }
  };

  // Suppression d'un article avec appel API
  const handleDeleteArticle = async (sectionId: string, articleId: string) => {
    if (!viewMode && setFormData) {
      const operationId = getOperationId();
      if (!operationId) {
        setError("ID d'opération introuvable.");
        clearMessageAfterDelay('error');
        return;
      }
      setLoadingArticleId(`${sectionId}-${articleId}`);
      setError(null);
      setSuccessMsg('');
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${operationId}/article/${articleId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) throw new Error("Erreur lors de la suppression de l'article");

        // Supprimer l'article du state local après confirmation API
        setFormData({
          ...formData,
          sections: sections.map(section =>
            section.id === sectionId
              ? { ...section, articles: section.articles.filter(article => article.id !== articleId) }
              : section
          )
        });
        setSuccessMsg("Article supprimé !");
        clearMessageAfterDelay('success');
      } catch (e: any) {
        setError(e.message || "Erreur lors de la suppression de l'article");
        clearMessageAfterDelay('error');
      } finally {
        setLoadingArticleId(null);
      }
    }
  };

  const validateForm = () => {
    if (viewMode) return true; // Pas de validation en mode lecture seule

    if (sections.length === 0) {
      setError('Vous devez créer au moins une section');
      return false;
    }

    const hasArticles = sections.some(section => section.articles.length > 0);
    if (!hasArticles) {
      setError('Vous devez créer au moins un article');
      return false;
    }

    setError(null);
    return true;
  };

  // Si pas de sections en mode consultation, afficher un message
  if (viewMode && (!sections || sections.length === 0)) {
    return (
      <div className="empty-sections-message">
        <p>Aucune section financière n'est disponible pour cette commande.</p>
      </div>
    );
  }

  // Ajouter deux nouvelles fonctions pour gérer les événements de touche
  const handleArticleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, sectionId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddArticle(sectionId);
    }
  };

  const handleSectionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSection();
    }
  };

  return (
    <div className={`step-container ${viewMode ? 'view-mode' : ''}`}>
      {!viewMode && (
        <>
          <h4>Détail du Prix Global et Forfaitaire (DPGF):</h4>
        </>
      )}

      {error && !viewMode && (
        <div className="error-message validation-error">
          {error}
        </div>
      )}

      {successMsg && !viewMode && (
        <div className="success-message">
          {successMsg}
        </div>
      )}

      <div className="sections-container">
        {sections.map((section) => (
          <div key={section.id} className="section">
            <div className="titre-article">
              <h5>{section.name}</h5>
              {!viewMode && (
                <button
                  className={`delete-button ${loadingSectionId === section.id ? 'loading' : ''}`}
                  onClick={() => handleDeleteSection(section.id)}
                  type="button"
                  disabled={loadingSectionId === section.id}
                >
                  {loadingSectionId === section.id ? (
                    <div className="button-loader"></div>
                  ) : (
                    '×'
                  )}
                </button>
              )}
            </div>

            <div className="articles-list">
              <table>
                <thead>
                  <tr>
                    <th className='numero-article'>N° article</th>
                    <th>Descriptif de l'article</th>
                    {!viewMode && <th style={{ width: '60px' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {section.articles.map((article) => (
                    <tr key={article.id}>
                      <td>{article.code}</td>
                      <td>{article.nom}</td>
                      {!viewMode && (
                        <td>
                          <button
                            className={`delete-button ${loadingArticleId === `${section.id}-${article.id}` ? 'loading' : ''}`}
                            onClick={() => handleDeleteArticle(section.id, article.id)}
                            type="button"
                            disabled={loadingArticleId === `${section.id}-${article.id}`}
                          >
                            {loadingArticleId === `${section.id}-${article.id}` ? (
                              <div className="button-loader"></div>
                            ) : (
                              '×'
                            )}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              {!viewMode && (
                activeSection === section.id ? (
                  <div className="add-article">
                    <input
                      type="text"
                      placeholder="Nom du nouvel article"
                      value={newArticleName}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 255) {
                          setNewArticleName(value);
                          setError(null);
                        } else {
                          setError("Le nom de l'article ne peut pas dépasser 255 caractères.");
                        }
                      }}
                      onKeyDown={(e) => handleArticleKeyDown(e, section.id)}
                    />
                    <button
                      className={`add-button ${loadingArticleId === section.id ? 'loading' : ''}`}
                      onClick={() => handleAddArticle(section.id)}
                      type="button"
                      disabled={loadingArticleId === section.id}
                    >
                      {loadingArticleId === section.id ? (
                        <div className="button-loader"></div>
                      ) : (
                        '+'
                      )}
                    </button>
                  </div>
                ) : (
                  <button
                    className="add-article-button"
                    onClick={() => setActiveSection(section.id)}
                    type="button"
                  >
                    Ajouter un article
                  </button>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      {!viewMode && (
        <>
          <div className="add-section">
            <span>Ajouter un titre: </span>
            <input
              type="text"
              placeholder="Nom du titre"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              onKeyDown={handleSectionKeyDown}
            />
            <button
              className={`add-button ${isAddingSectionLoading ? 'loading' : ''}`}
              onClick={handleAddSection}
              type="button"
              disabled={isAddingSectionLoading}
            >
              {isAddingSectionLoading ? (
                <div className="button-loader"></div>
              ) : (
                '+'
              )}
            </button>
          </div>

          <div className="total-section">
            <p>Total {sections.reduce((acc, section) => acc + section.articles.length, 0)} articles</p>
          </div>
        </>
      )}

      {!viewMode && onNext && onPrevious && (
        <div className="action-buttons">
          <Button
            value="Imprimer"
            variant="secondary"
            onClick={() => {
              // Fonctionnalité pour générer un PDF des articles
              console.log("Impression du descriptif financier");
            }}
          />
          <Button
            value={isSaving ? "Enregistrement..." : "Enregistrer en brouillon"}
            variant="secondary"
            onClick={() => handleSaveDraft(
              formData,
              '/api/marche-subsequent',
              setIsSaving,
              setSuccessMsg
            )}
            disabled={isSaving}
          />
          <Button value="Précédent" variant="secondary" onClick={onPrevious} />
          <Button
            value="Suivant"
            variant="primary"
            onClick={() => handleValidatedNext(validateForm, onNext)}
          />
          {isSaving && (
            <div style={{ textAlign: 'center', margin: '2rem 0' }}>
              <div className="loader" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CMStepThree;